import { useState } from 'react';
import ReactMarkdown from 'react-markdown';

/**
 * Chat Message Component
 * Displays user or AI messages with markdown support
 */
export default function ChatMessage({ message, isUser, timestamp }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!isUser && message) {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const date = new Date(time);
    return date.toLocaleTimeString('ar-AE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 16,
      animation: 'fadeIn 0.3s ease'
    }}>
      {/* Avatar for AI */}
      {!isUser && (
        <div style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #4ecdc4, #38b2ac)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          marginRight: 12,
          flexShrink: 0
        }}>
          🎓
        </div>
      )}

      {/* Message Bubble */}
      <div style={{
        maxWidth: '75%',
        display: 'flex',
        flexDirection: 'column',
        gap: 4
      }}>
        <div style={{
          padding: '12px 16px',
          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          background: isUser 
            ? 'linear-gradient(135deg, #4ecdc4, #38b2ac)' 
            : '#ffffff',
          border: isUser ? 'none' : '1px solid #e2e8f0',
          color: '#000000',
          fontSize: 14,
          lineHeight: 1.6,
          wordWrap: 'break-word'
        }}>
          {isUser ? (
            <div style={{ whiteSpace: 'pre-wrap' }}>{message}</div>
          ) : (
            <div className="markdown-content">
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p style={{ margin: '8px 0' }}>{children}</p>,
                  ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: 20 }}>{children}</ul>,
                  ol: ({ children }) => <ol style={{ margin: '8px 0', paddingLeft: 20 }}>{children}</ol>,
                  li: ({ children }) => <li style={{ margin: '4px 0' }}>{children}</li>,
                  code: ({ children }) => (
                    <code style={{
                      background: '#ffffff',
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: 13,
                      fontFamily: 'monospace'
                    }}>
                      {children}
                    </code>
                  ),
                  pre: ({ children }) => (
                    <pre style={{
                      background: '#ffffff',
                      padding: 12,
                      borderRadius: 8,
                      overflow: 'auto',
                      margin: '8px 0'
                    }}>
                      {children}
                    </pre>
                  ),
                  strong: ({ children }) => <strong style={{ fontWeight: 700, color: '#000000' }}>{children}</strong>,
                  em: ({ children }) => <em style={{ fontStyle: 'italic', color: '#000000' }}>{children}</em>,
                }}
              >
                {message}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Timestamp and Actions */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 4px'
        }}>
          {timestamp && (
            <span style={{ fontSize: 11, color: '#4d6680' }}>
              {formatTime(timestamp)}
            </span>
          )}
          
          {!isUser && (
            <button
              onClick={handleCopy}
              style={{
                background: 'transparent',
                border: 'none',
                color: copied ? '#10b981' : '#4d6680',
                cursor: 'pointer',
                fontSize: 11,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 6px',
                borderRadius: 4,
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {copied ? '✅ تم النسخ' : '📋 نسخ'}
            </button>
          )}
        </div>
      </div>

      {/* Avatar for User */}
      {isUser && (
        <div style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981, #059669)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          marginLeft: 12,
          flexShrink: 0
        }}>
          👤
        </div>
      )}
    </div>
  );
}
